import React from 'react';
import { Button } from './ui/button';
import { heroData, trustBadges, stats } from '../data/mock';
import { ShieldCheck, MapPin, Clock, Heart, ChevronRight, Star } from 'lucide-react';

const iconMap = {
  ShieldCheck,
  MapPin,
  Clock,
  Heart
};

const HeroSection = ({ onGetQuote }) => {
  return (
    <section className="relative pt-20 md:pt-24 overflow-hidden bg-gradient-to-b from-amber-50/50 to-white" aria-label="Dog Boarding Baltimore Hero">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-64 h-64 bg-amber-200 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-200 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-md mb-6">
              <div className="flex" aria-label="5 star rating">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" aria-hidden="true" />
                ))}
              </div>
              <span className="text-sm font-medium text-stone-600">Trusted by 50+ Baltimore Dog Owners</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 leading-tight mb-6">
              Dog Boarding Baltimore – Your #1 Trusted Local Pet Care
            </h1>
            
            <p className="text-lg md:text-xl text-stone-600 mb-8 leading-relaxed max-w-xl">
              Looking for <strong>dog boarding in Baltimore</strong>? We're locally owned & operated with 100% background-checked staff. Trusted by your neighbors for 5+ years. Overnight boarding, daycare & extended stays available.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                onClick={onGetQuote}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all group"
                aria-label="Get a free dog boarding quote"
              >
                {heroData.ctaText}
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.querySelector('#services').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-stone-300 text-stone-700 hover:border-amber-500 hover:text-amber-600 font-semibold px-8 py-6 text-lg rounded-xl transition-all"
                aria-label="View our dog boarding services"
              >
                View Services
              </Button>
            </div>

            {/* Mini Trust Badges */}
            <div className="grid grid-cols-2 gap-4">
              {trustBadges.slice(0, 2).map((badge) => {
                const IconComponent = iconMap[badge.icon];
                return (
                  <div key={badge.id} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-amber-600" aria-hidden="true" />
                    </div>
                    <span className="text-sm font-medium text-stone-700">{badge.title}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right - Hero Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={heroData.image}
                alt="Happy golden retriever at Dog Boarding Baltimore facility - trusted pet care in Baltimore MD"
                className="w-full h-[400px] md:h-[500px] object-cover"
                loading="eager"
                width="600"
                height="500"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <ShieldCheck className="w-6 h-6 text-green-600" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800">100% Background Checked Staff</p>
                      <p className="text-sm text-stone-500">Every team member verified for your peace of mind</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-amber-500 text-white rounded-2xl px-4 py-2 shadow-lg transform rotate-3">
              <p className="font-bold text-lg">5+ Years</p>
              <p className="text-xs text-amber-100">Serving Baltimore</p>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg border border-stone-100 p-6 md:p-8" aria-label="Dog Boarding Baltimore statistics">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-amber-600">{stat.value}</p>
                <p className="text-sm text-stone-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
